import React, { useRef } from 'react';
import useBreakpoints from '@hooks/useBreakpoints';
import useTheme from '@cvent/blocks/hooks/useTheme';
import Flyout from '@components/shared/Flyout';
import { injectTestId } from '@cvent/nucleus-test-automation';
import { Col } from '@cvent/carina/components/Col';
import { Row } from '@cvent/carina/components/Row';
import { Placements } from '@cvent/carina/components/Popover';
import useNavFlyoutPlacement from '@hooks/useNavFlyoutPlacement';
import { CSSObject } from '@emotion/react';
import { MoodThemeProvider } from '@components/blocks/BlockThemeProvider';
import { isMobile, isTablet } from 'react-device-detect';

const useStyles = (isFlyoutVisible): Record<string, CSSObject> => {
  const { isMobile } = useBreakpoints();
  const { backgroundColor, font } = useTheme();
  return {
    triggerButton: {
      textDecoration: 'none',
      cursor: 'pointer',
      borderRadius: 10,
      padding: '10px 13px 9px 13px',
      position: 'relative',
      border: 'none',
      backgroundColor: 'transparent',
      '&:hover': {
        backgroundColor: !isFlyoutVisible ? backgroundColor.brand.hover : null
      },
      '&:focus': {
        backgroundColor: isFlyoutVisible ? backgroundColor.brand.focus : null,
        // imitating Chrome default focus outline in Firefox
        '@-moz-document url-prefix()': {
          outline: '2px solid blue',
          boxShadow: '0 0 0 3px white'
        }
      }
    },
    icon: {
      color: !isMobile ? font.color.base : font.color.neutral.base
    },
    activeButton: {
      backgroundColor: backgroundColor.brand.hover
    },
    mobileTitle: {
      color: font.color.base,
      fontSize: font.base.size.m,
      paddingLeft: 12
    }
  };
};

interface Props {
  accessibilityLabel: string;
  icon: React.ReactNode;
  testID: string;
  onFlyoutVisible?: (isVisible: boolean) => void;
  isFlyoutVisible: boolean;
  // title is only shown in mobile breakpoints
  title?: string;
}

/**
 * Shared component for building icon flyouts for the nav
 */
function NavigationFlyoutControl({
  children,
  accessibilityLabel,
  icon,
  testID,
  onFlyoutVisible = () => null,
  isFlyoutVisible,
  title
}: React.PropsWithChildren<Props>): JSX.Element {
  const toggleVisible = (isVisible: boolean): void => {
    onFlyoutVisible(isVisible);
  };
  const styles = useStyles(isFlyoutVisible);
  const iconRef = useRef(null);
  const { size } = useTheme();
  const { isMobile: isMobileBreakpoint } = useBreakpoints();

  // align right edge of flyout to right edge of profile icon (desktop) or menu icon (tablet)
  const placementOffset = useNavFlyoutPlacement(iconRef?.current?.getBoundingClientRect()?.right || 0, isFlyoutVisible);

  return (
    <Flyout
      triggerMethod={isMobile || isTablet ? 'CLICK' : 'HOVER'}
      trigger={({ toggleOpen, isVisible }) => (
        <Row>
          <Col padding={0} width="content">
            <button
              type="button"
              onClick={() => {
                toggleOpen();
              }}
              css={[
                { ...styles.triggerButton, ...(isMobileBreakpoint && { padding: 0 }) },
                isVisible ? styles.activeButton : null
              ]}
              {...injectTestId(testID)}
              aria-label={accessibilityLabel}
              aria-expanded={isFlyoutVisible}
              ref={iconRef}
            >
              {isMobileBreakpoint ? (
                <Row>
                  <Col padding={0} width="content">
                    {icon}
                  </Col>
                  <Col
                    flex={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    padding={0}
                    width="content"
                  >
                    <div css={styles.mobileTitle}>{title}</div>
                  </Col>
                </Row>
              ) : (
                icon
              )}
            </button>
          </Col>
        </Row>
      )}
      placement={Placements.bottomEnd}
      placementOffset={{ left: placementOffset, top: 16 }}
      toggleVisible={toggleVisible}
      isVisible={isFlyoutVisible}
      zIndex={1000}
      hasSpacerAfterTrigger
    >
      <MoodThemeProvider css={{ borderRadius: size.border.radius.fiveX }}>{children}</MoodThemeProvider>
    </Flyout>
  );
}

export default NavigationFlyoutControl;
